import { TOfferRequest } from "@gp/hub";
import { PeriodConverter } from "@state";
import {
	IReactionDisposer,
	action,
	computed,
	observable,
	reaction,
	runInAction,
} from "mobx";

import { THEME_SETTINGS } from "@theme-settings";
import PrematchOfferApiService from "@api-services/offer/PrematchOfferApiService";
import { Period } from "@state/common/PeriodConverter";
import { LiveStatus } from "@gp/models";
import { FavoritesStore as GPFavoriteStore } from "@gp/offer";
import { FavoritesApiService } from "@api-services/favorites/FavoritesApiService";
import { FavoriteEventsCounterStore } from "./FavoriteEventsCounterStore";
import { FavoriteTournamentsCounterStore } from "./FavoriteTournamentCounterStore";
import type { RootStore } from "@state/stores/types";

export default class FavoriteStore extends GPFavoriteStore {
	favoriteEventsStoreCounter: FavoriteEventsCounterStore;
	favoriteTournamentsCounterStore: FavoriteTournamentsCounterStore;

	@observable period: Period = "full";
	@observable isStoreInitialized: boolean = false;

	prematchFavoriteEventIdsReaction: IReactionDisposer | null;
	liveFavoriteEventIdsReaction: IReactionDisposer | null;

	rootStore: RootStore;
	constructor(rootStore: RootStore) {
		super({
			httpClient: new FavoritesApiService(),
			notificationProvider: rootStore.notificationStore,
		});
		this.rootStore = rootStore;
		this.favoriteEventsStoreCounter = new FavoriteEventsCounterStore(
			rootStore,
			this
		);
		this.favoriteTournamentsCounterStore =
			new FavoriteTournamentsCounterStore(rootStore, this);
	}

	@computed get liveFavoritesCounter() {
		return this.favoriteEventsStoreCounter.liveFavoritesCount;
	}

	@computed get prematchFavoritesCount() {
		return this.favoriteEventsStoreCounter.prematchFavoritesCount;
	}

	@computed get favoriteTournamentsCount() {
		return this.favoriteTournamentsCounterStore.tournamentFavoritesCount;
	}

	@action.bound
	public async fetchUserFavorites() {
		if (
			THEME_SETTINGS.features.isFavoritesEnabled &&
			this.rootStore.userAuthStore.isLoggedIn
		) {
			await this.getUserFavorites();
		}
		this.isStoreInitialized = true;
	}

	@action.bound
	public async onInitializePrematchFavorites() {
		this.favoriteTournamentsCounterStore.onInitialize();
		this.prematchFavoriteEventIdsReaction = reaction(
			() => ({
				userFavoriteEventsSet: this.userFavoriteEventsSet,
				period: this.period,
			}),
			(props) => {
				if (PeriodConverter.IsPredefinedPeriod(props.period)) {
					runInAction(() => {
						this.favoriteEventsStoreCounter.fetchPrematchFavoritesCounter();
					});
				} else {
					this.favoriteEventsStoreCounter.prematchFavoritesCount = 0;
				}
			},
			{
				fireImmediately: true,
			}
		);
	}

	@action.bound
	public onInitializeLiveFavorites() {
		this.liveFavoriteEventIdsReaction = reaction(
			() => ({ userFavoriteEventsSet: this.userFavoriteEventsSet }),
			() => {
				runInAction(() => {
					this.favoriteEventsStoreCounter.fetchLiveFavoritesCounter();
				});
			},
			{
				fireImmediately: true,
			}
		);
	}

	@action.bound
	async deselectFavoritesEvents(liveStatus: LiveStatus) {
		if (this.userFavoriteEventsSet.size === 0) {
			return;
		}

		const favFilter: TOfferRequest = {
			filter: [
				{
					id: {
						eq: [...this.userFavoriteEventsSet],
					},
					liveStatus: liveStatus,
					offers: null,
				},
			],
		};

		try {
			const data = await PrematchOfferApiService.getOfferData(favFilter);
			let eventsIds: string[] | null = null;

			if (data.offer) {
				eventsIds = data?.offer.map((event: any) => event.eventId);
			}

			if (!eventsIds) {
				return;
			}

			await this.removeMultipleUserFavoriteEvents(eventsIds);
		} catch (err) {
			console.error(err);
		}
	}

	@action.bound
	async deselectFavoritesTournaments() {
		if (this.userFavoriteTournamentsSet.size === 0) {
			return;
		}

		try {
			await this.removeMultipleUserFavoriteTournaments([
				...this.userFavoriteTournamentsSet,
			]);
		} catch (err) {
			console.error(err);
		}
	}

	@action.bound
	async deselectFavoriteTournamentsLive(tournamentIds: string[]) {
		if (tournamentIds.length == 0) {
			return;
		}

		try {
			await this.removeMultipleUserFavoriteTournaments([
				...tournamentIds,
			]);
		} catch (err) {
			console.error(err);
		}
	}
	@action.bound
	async deselectFavoriteEventLive(eventIds: string[]) {
		if (eventIds.length == 0) {
			return;
		}

		try {
			await this.removeMultipleUserFavoriteEvents([...eventIds]);
		} catch (err) {
			console.error(err);
		}
	}

	@action.bound
	onDispose() {
		this.disposePrematchReaction();
		this.disposeLiveReaction();
		this.favoriteEventsStoreCounter.onDispose();
		this.reset();
		this.isStoreInitialized = false;
	}

	@action.bound
	disposePrematchReaction() {
		if (this.prematchFavoriteEventIdsReaction != null) {
			this.prematchFavoriteEventIdsReaction();
			this.prematchFavoriteEventIdsReaction = null;
		}
		this.favoriteEventsStoreCounter.onDispose();
		this.favoriteTournamentsCounterStore.onDispose();
	}

	@action.bound
	disposeLiveReaction() {
		if (this.liveFavoriteEventIdsReaction != null) {
			this.liveFavoriteEventIdsReaction();
			this.liveFavoriteEventIdsReaction = null;
		}
		this.favoriteEventsStoreCounter.onDispose();
	}

	//#region prematch favorites betting types

	//#region utility

	@action.bound
	setPeriod(period: Period) {
		if (THEME_SETTINGS.features.isFavoritesEnabled) {
			this.period = period;
		}
	}

	getTime() {
		if (this.period == "full") {
			return {};
		}
		return {
			startTime: PeriodConverter.CalculateOfferInterval(this.period),
		};
	}

	//#endregion utility
}
